import { readTokenColor } from "@/lib/color-scheme/computed"

const STORY_WIDTH = 1080
const STORY_HEIGHT = 1920

const MAX_TILES = 3
const TILE_PADDING = 14
const TILE_RADIUS = 42
const PHOTO_RADIUS = 28
const LOGO_SRC = "/images/nelo-logo-white.svg"
const LOGO_TOP = 252
const LOGO_HEIGHT = 44

export interface StoryCopy {
  headlineLead: string
  headlineEmphasis: string
  offer: string
  stickerHint: string
  codeLine: string
}

interface StoryTile {
  x: number
  y: number
  width: number
  height: number
}

const TILE_LAYOUTS: Record<number, readonly StoryTile[]> = {
  1: [{ x: 346, y: 680, width: 388, height: 398 }],
  2: [
    { x: 207, y: 650, width: 368, height: 377 },
    { x: 491, y: 763, width: 384, height: 387 },
  ],
  3: [
    { x: 112, y: 724, width: 340, height: 349 },
    { x: 628, y: 724, width: 340, height: 349 },
    { x: 356, y: 650, width: 368, height: 400 },
  ],
}

const storyTileLayout = (count: number): readonly StoryTile[] => TILE_LAYOUTS[Math.min(Math.max(count, 0), MAX_TILES)] ?? []

const storyImageSource = (url: string): string => `/_next/image?url=${encodeURIComponent(url)}&w=1080&q=90`

const loadImage = (src: string): Promise<HTMLImageElement | null> =>
  new Promise((resolve) => {
    const image = new Image()
    image.crossOrigin = "anonymous"
    image.decoding = "async"
    image.onload = () => resolve(image)
    image.onerror = () => resolve(null)
    image.src = src
  })

const loadProductImage = async (url: string): Promise<HTMLImageElement | null> => (await loadImage(storyImageSource(url))) ?? (await loadImage(url))

const fontFamily = (variable: string, fallback: string): string => getComputedStyle(document.documentElement).getPropertyValue(variable).trim() || fallback

const roundedRect = (context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) => {
  context.beginPath()
  context.roundRect(x, y, width, height, radius)
}

const PHOTO_INSET = 28

const drawContain = (context: CanvasRenderingContext2D, image: HTMLImageElement, x: number, y: number, width: number, height: number) => {
  const box = { width: width - PHOTO_INSET * 2, height: height - PHOTO_INSET * 2 }
  const scale = Math.min(box.width / image.naturalWidth, box.height / image.naturalHeight)
  const drawWidth = image.naturalWidth * scale
  const drawHeight = image.naturalHeight * scale
  context.drawImage(image, x + (width - drawWidth) / 2, y + (height - drawHeight) / 2, drawWidth, drawHeight)
}

const drawBackground = (context: CanvasRenderingContext2D) => {
  const gradient = context.createLinearGradient(0, 0, 0, STORY_HEIGHT)
  const deep = readTokenColor("--color-story-deep")
  gradient.addColorStop(0, deep)
  gradient.addColorStop(0.098, deep)
  gradient.addColorStop(0.429, readTokenColor("--color-story-blue"))
  gradient.addColorStop(1, readTokenColor("--color-story-sky"))
  context.fillStyle = gradient
  context.fillRect(0, 0, STORY_WIDTH, STORY_HEIGHT)
}

const drawTile = (context: CanvasRenderingContext2D, tile: StoryTile, image: HTMLImageElement | null) => {
  context.save()
  context.shadowColor = readTokenColor("--color-story-tile-shadow")
  context.shadowBlur = 48
  context.shadowOffsetY = 18
  roundedRect(context, tile.x, tile.y, tile.width, tile.height, TILE_RADIUS)
  context.fillStyle = readTokenColor("--color-story-card")
  context.fill()
  context.restore()

  context.save()
  roundedRect(context, tile.x + 1, tile.y + 1, tile.width - 2, tile.height - 2, TILE_RADIUS - 1)
  context.strokeStyle = readTokenColor("--color-story-tile-rim")
  context.lineWidth = 2
  context.stroke()
  context.restore()

  const inner = { x: tile.x + TILE_PADDING, y: tile.y + TILE_PADDING, width: tile.width - TILE_PADDING * 2, height: tile.height - TILE_PADDING * 2 }
  context.save()
  roundedRect(context, inner.x, inner.y, inner.width, inner.height, PHOTO_RADIUS)
  context.fillStyle = readTokenColor("--color-story-ink")
  context.fill()
  context.clip()
  if (image) drawContain(context, image, inner.x, inner.y, inner.width, inner.height)
  context.restore()
}

const drawText = (context: CanvasRenderingContext2D, text: string, y: number, font: string, color: string) => {
  context.font = font
  context.fillStyle = color
  context.fillText(text, STORY_WIDTH / 2, y, STORY_WIDTH - 160)
}

const OFFER_TOP = 1288
const OFFER_LINE = 76

const offerLines = (context: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
  const words = text.split(" ")
  const width = (line: string) => context.measureText(line).width
  if (width(text) <= maxWidth) return [text]
  let best: string[] = []
  let bestWidth = Number.POSITIVE_INFINITY
  for (let split = 1; split < words.length; split += 1) {
    const lines = [words.slice(0, split).join(" "), words.slice(split).join(" ")]
    const widest = Math.max(...lines.map(width))
    if (widest < bestWidth) {
      best = lines
      bestWidth = widest
    }
  }
  return best
}

const drawSticker = (context: CanvasRenderingContext2D, hint: string, inter: string) => {
  context.save()
  roundedRect(context, 258, 1436, 570, 79, 22)
  context.setLineDash([14, 10])
  context.lineWidth = 3
  context.strokeStyle = readTokenColor("--color-story-sticker")
  context.stroke()
  context.restore()
  context.textBaseline = "middle"
  drawText(context, hint, 1476, `500 39px ${inter}`, readTokenColor("--color-story-hint"))
  context.textBaseline = "alphabetic"
}

export const renderStoryImage = async (imageUrls: readonly string[], copy: StoryCopy): Promise<Blob> => {
  const display = fontFamily("--font-bricolage", "sans-serif")
  const inter = fontFamily("--font-inter", "sans-serif")
  const urls = imageUrls.slice(0, MAX_TILES)
  const [logo, images] = await Promise.all([
    loadImage(LOGO_SRC),
    Promise.all(urls.map(loadProductImage)),
    document.fonts.load(`500 76px ${display}`).catch(() => []),
    document.fonts.load(`500 40px ${inter}`).catch(() => []),
  ])

  const canvas = document.createElement("canvas")
  canvas.width = STORY_WIDTH
  canvas.height = STORY_HEIGHT
  const context = canvas.getContext("2d")
  if (!context) throw new Error("Canvas is unavailable")

  drawBackground(context)
  if (logo) {
    const width = (logo.naturalWidth / logo.naturalHeight) * LOGO_HEIGHT
    context.drawImage(logo, (STORY_WIDTH - width) / 2, LOGO_TOP, width, LOGO_HEIGHT)
  }

  context.textAlign = "center"
  const ink = readTokenColor("--color-story-ink")
  drawText(context, copy.headlineLead, 428, `500 76px ${display}`, readTokenColor("--color-story-ink-muted"))
  drawText(context, copy.headlineEmphasis, 532, `500 76px ${display}`, ink)

  const tiles = storyTileLayout(urls.length)
  tiles.forEach((tile, index) => drawTile(context, tile, images[index] ?? null))

  const offerFont = `600 54px ${display}`
  context.font = offerFont
  offerLines(context, copy.offer, STORY_WIDTH - 160).forEach((line, index) => drawText(context, line, OFFER_TOP + index * OFFER_LINE, offerFont, ink))
  drawSticker(context, copy.stickerHint, inter)
  drawText(context, copy.codeLine, 1621, `600 40px ${inter}`, ink)

  return new Promise((resolve, reject) => canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Story image could not be encoded"))), "image/png"))
}
